import React, { useState, useEffect } from 'react';
import styles from './TimeRangePicker.module.css';

const toHHMM = (date) => {
  const d = new Date(date);
  if (isNaN(d)) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// hideRangeError: oculta el aviso de horario inválido mientras el usuario todavía está eligiendo el día
// de devolución (con el primer clic retiro y devolución quedan el mismo día y las horas precargadas de
// una solicitud editada pueden no calzar). La validación (isValid) no cambia.
// onUserEdit: se llama cuando el usuario cambia una hora (no al precargar las de la solicitud).
const TimeRangePicker = ({ onChange, startDate, endDate, initialStartDate, initialEndDate, hideRangeError = false, onUserEdit }) => {
  const [times, setTimes] = useState({
    startTime: initialStartDate ? toHHMM(initialStartDate) : '',
    endTime: initialEndDate ? toHHMM(initialEndDate) : ''
  });

  const getFullTimestamp = (date, timeStr) => {
    if (!date || !timeStr) return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    const timestamp = new Date(date);
    timestamp.setHours(hours, minutes, 0, 0);
    return timestamp;
  };

  const notifyChange = (newTimes) => {
    if (newTimes.startTime && newTimes.endTime && startDate && endDate) {
      const startTimestamp = getFullTimestamp(startDate, newTimes.startTime);
      const endTimestamp = getFullTimestamp(endDate, newTimes.endTime);

      onChange({
        startTime: newTimes.startTime,
        endTime: newTimes.endTime,
        isValid: startTimestamp && endTimestamp && endTimestamp > startTimestamp
      });
    } else {
      onChange({
        startTime: newTimes.startTime,
        endTime: newTimes.endTime,
        isValid: false
      });
    }
  };

  // avisar a padre que el valor ha cambiado, hidratar el estado
  useEffect(() => {
    if (initialStartDate && initialEndDate) {
      const hydrated = { startTime: toHHMM(initialStartDate), endTime: toHHMM(initialEndDate) };
      setTimes(hydrated);
      notifyChange(hydrated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStartDate, initialEndDate, startDate, endDate]);

  const handleTimeChange = (field, value) => {
    if (onUserEdit) onUserEdit();
    const newTimes = { ...times, [field]: value };
    setTimes(newTimes);
    notifyChange(newTimes);
  };

  const isValidTimeRange = () => {
    if (!times.startTime || !times.endTime || !startDate || !endDate) return true;
    
    const startTimestamp = getFullTimestamp(startDate, times.startTime);
    const endTimestamp = getFullTimestamp(endDate, times.endTime);
    
    return startTimestamp && endTimestamp && endTimestamp > startTimestamp;
  };


  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <h2 className={`text-xl font-semibold text-white text-center uppercase ${styles.titleText}`}>
          Horarios
        </h2>
      </div>

      <div className={styles.content}>
        <div className={`space-y-8 ${styles.fields}`}>
          <div className={`time-input-group ${styles.group}`}>
            <label className={`block text-sm font-medium mb-3 uppercase ${styles.label}`}>
              <b>Retiro:</b> {startDate && (
                  new Date(startDate).toLocaleDateString('es-ES', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })
              )}
            </label>
            <div className={`relative ${styles.inputWrapper}`}>
              <input
                type="time"
                value={times.startTime}
                onChange={(e) => handleTimeChange('startTime', e.target.value)}
                className={`w-full px-6 py-4 border-2 border-[#1AA29B] rounded-[40px] focus:ring-2 focus:ring-[#1AA29B] focus:border-[#1AA29B] transition-colors text-lg ${styles.timeInput}`}
                step="300"
              />
            </div>
          </div>

          <div className={`time-input-group ${styles.group}`}>
            <label className={`block text-sm font-medium mb-3 uppercase ${styles.label}`}>
              <b>Devolución:</b> {endDate && (
                  new Date(endDate).toLocaleDateString('es-ES', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })
              )}
            </label>
            <div className={`relative ${styles.inputWrapper}`}>
              <input
                type="time"
                value={times.endTime}
                onChange={(e) => handleTimeChange('endTime', e.target.value)}
                className={`w-full px-6 py-4 border-2 border-[#1AA29B] rounded-[40px] focus:ring-2 focus:ring-[#1AA29B] focus:border-[#1AA29B] transition-colors text-lg ${styles.timeInput}`}
                step="300"
              />
            </div>
          </div>

          {!hideRangeError && !isValidTimeRange() && (
            <div className={`text-red-500 text-sm bg-red-50 p-4 rounded-[40px] text-center ${styles.rangeError}`}>
              El período seleccionado debe comenzar antes de que termine
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeRangePicker;
